import { computed, toValue } from "vue";

// Find an option's label/title by its value (string-compared so "" and numbers
// match). Returns undefined when the value is null or unmatched.
function findOptionLabel(options, value, titleKey) {
  if (value == null) {
    return undefined;
  }
  return options.find((option) => String(option.value) === String(value))?.[titleKey];
}

/**
 * Derive the Provider filter chip's read-only summary sentence from its selected
 * sub-values. Pure projection over reactive sources, extracted so the sentence
 * logic is unit-testable without mounting the SFC.
 *
 * @param {Object} sources - reactive refs/computeds:
 *   `newPatient`, `name`, `providerType`, `affiliation` (selection refs);
 *   `newPatientItems`, `providerTypeItems`, `affiliationOptions` (option lists);
 *   `affiliationSingle`, `affiliationSingleName` (single-affiliation config);
 *   `namedLabel`, `providerLabel` (translated sentence fragments).
 * @returns {{ hasActiveSelection, newPatientPhrase, providerTypeText, affiliationText, activatorTitle }}
 */
export function useProviderFilterSummary({
  newPatient,
  name,
  providerType,
  affiliation,
  newPatientItems,
  providerTypeItems,
  affiliationSingle,
  affiliationSingleName,
  affiliationOptions,
  namedLabel,
  providerLabel,
}) {
  // True when any real filter is set. "Any provider" on its own (new_patient '')
  // is not a filter, so the chip stays inactive until a name, type, affiliation,
  // or a specific new/returning choice is made.
  const hasActiveSelection = computed(() =>
    Boolean(newPatient.value || name.value || providerType.value || affiliation.value),
  );

  // The "I'm looking for" radio label backing the summary sentence. An empty/Any
  // selection resolves to "Any provider" (the default-checked radio); a wholly
  // unmatched value falls back to the generic "A provider".
  const newPatientPhrase = computed(
    () => findOptionLabel(newPatientItems.value, newPatient.value ?? "", "label") || "A provider",
  );

  const providerTypeText = computed(() => findOptionLabel(providerTypeItems.value, providerType.value, "title"));

  const affiliationText = computed(() => {
    if (!affiliation.value) {
      return undefined;
    }
    return affiliationSingle.value
      ? affiliationSingleName.value
      : findOptionLabel(affiliationOptions.value, affiliation.value, "title");
  });

  // Chip summary as a readable sentence: start from the "I'm looking for" phrase,
  // swap the provider noun for the chosen provider type, append the typed name
  // with the translated connective, then append the affiliation.
  const activatorTitle = computed(() => {
    if (!hasActiveSelection.value) {
      return undefined;
    }

    const named = toValue(namedLabel) ?? "named";
    const providerNoun = toValue(providerLabel) ?? "provider";

    let summary = newPatientPhrase.value;
    if (providerTypeText.value) {
      // Swap the generic noun for the chosen type. The function replacer inserts
      // `$`-sequences in a CMS-configured title (`$&`, `$1`, `$$`) verbatim rather
      // than as patterns. When the phrase carries no noun to swap (a localized
      // label, or an unmatched new_patient value), append the type so it's never
      // dropped from the summary.
      if (providerNoun && summary.includes(providerNoun)) {
        summary = summary.replace(providerNoun, () => providerTypeText.value);
      } else {
        summary = `${summary}: ${providerTypeText.value}`;
      }
    }
    if (name.value) {
      summary = `${summary} ${named} ${name.value}`;
    }
    if (affiliationText.value) {
      summary = `${summary}, ${affiliationText.value}`;
    }
    return summary;
  });

  return { hasActiveSelection, newPatientPhrase, providerTypeText, affiliationText, activatorTitle };
}
